import React, { useCallback, useEffect, useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { AlignCenter, AlignLeft, AlignRight, MoveHorizontal } from 'lucide-react';

interface ResizableImageAttrs {
  src: string;
  alt?: string;
  title?: string;
  width?: string;
  height?: string;
  alignment?: 'left' | 'center' | 'right';
}

// Using any type for NodeViewProps as a workaround
const ResizableImageComponent: React.FC<any> = ({ 
  node, 
  updateAttributes,
  selected,
}) => {
  const attrs = node.attrs as ResizableImageAttrs;
  
  const [isResizing, setIsResizing] = useState(false);
  const [initialWidth, setInitialWidth] = useState(0);
  const [initialX, setInitialX] = useState(0);

  // Handle image alignment
  const setAlignment = useCallback((alignment: 'left' | 'center' | 'right', e: React.MouseEvent) => {
    // Stop propagation to prevent the click from bubbling up to form elements
    e.preventDefault();
    e.stopPropagation();
    
    updateAttributes({ alignment });
  }, [updateAttributes]);

  // Handle mouse down for resizing
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get image element
    const imageElement = e.currentTarget.parentElement?.querySelector('img');
    if (!imageElement) return;
    
    // Set initial states for resizing
    setIsResizing(true);
    setInitialWidth(imageElement.clientWidth);
    setInitialX(e.clientX);
  };

  // Handle mouse move for resizing
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    // Calculate the width difference
    const widthChange = e.clientX - initialX;
    const newWidth = initialWidth + widthChange;
    
    // Convert to percentage relative to parent
    const imageElement = document.querySelector('.resize-image-handle')?.parentElement?.querySelector('img');
    if (!imageElement) return;
    
    const parentWidth = imageElement.parentElement!.clientWidth;
    const widthPercentage = Math.max(10, Math.min(100, (newWidth / parentWidth) * 100));
    
    updateAttributes({ width: `${widthPercentage}%` });
  }, [isResizing, initialWidth, initialX, updateAttributes]);

  // Handle mouse up for resizing
  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
    }
  }, [isResizing]);

  // Set up event listeners for resize drag
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Apply alignment to wrapper instead of image
  return (
    <NodeViewWrapper className="resizable-image-wrapper" data-alignment={attrs.alignment || 'center'}>
      <div className="relative group" style={{ 
          width: attrs.width || '100%',
          // Container should handle the alignment, not the img
          margin: attrs.alignment === 'center' 
            ? '0 auto' 
            : attrs.alignment === 'left' 
              ? '0 auto 0 0' 
              : '0 0 0 auto'
        }}>
        <img 
          src={attrs.src} 
          alt={attrs.alt || ''} 
          title={attrs.title || ''} 
          style={{ 
            display: 'block',
            height: attrs.height || 'auto',
            width: '100%'
            // Removed margin from here - alignment handled by parent
          }}
          draggable="false"
          className="rounded-lg max-w-full"
        />

        {/* Image controls appear when the node is selected */}
        {selected && (
          <div 
            className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-background border border-input px-2 py-1 rounded-md shadow-md flex items-center gap-1 z-50"
            onClick={(e) => e.stopPropagation()} // Stop clicks on the toolbar from propagating
          >
            <button 
              onClick={(e) => setAlignment('left', e)}
              className={`p-1 rounded hover:bg-secondary ${attrs.alignment === 'left' ? 'bg-secondary' : ''}`}
              title="Align left"
              type="button" // Explicitly set type to avoid form submission
            >
              <AlignLeft size={16} />
            </button>
            <button 
              onClick={(e) => setAlignment('center', e)}
              className={`p-1 rounded hover:bg-secondary ${attrs.alignment === 'center' ? 'bg-secondary' : ''}`}
              title="Align center"
              type="button" // Explicitly set type to avoid form submission
            >
              <AlignCenter size={16} />
            </button>
            <button 
              onClick={(e) => setAlignment('right', e)}
              className={`p-1 rounded hover:bg-secondary ${attrs.alignment === 'right' ? 'bg-secondary' : ''}`}
              title="Align right"
              type="button" // Explicitly set type to avoid form submission
            >
              <AlignRight size={16} />
            </button>
          </div>
        )}

        {/* Resize handle */}
        {selected && (
          <div
            className="resize-image-handle absolute bottom-2 right-2 w-6 h-6 bg-primary/80 rounded-full flex items-center justify-center cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity"
            onMouseDown={handleMouseDown}
            title="Resize image"
          >
            <MoveHorizontal size={14} className="text-white" />
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export default ResizableImageComponent; 